import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { publicOrigin } from '@/lib/publicOrigin';

/**
 * Fetches the user's Defera Status (active tier + in-progress next-year tier).
 *
 * Returns:
 *   {
 *     status: {
 *       active: { tier, qd_dollars, qt_count, granted_via, qualifying_year,
 *                 benefit_period_start, benefit_period_end } | null,
 *       in_progress: { qualifying_year, projected_tier, qd_dollars, qt_count,
 *                      next_tier, dollars_to_next, percent_to_next }
 *     } | null,
 *     isLoading: boolean,
 *     refetch: () => Promise<void>
 *   }
 *
 * Mirrors the resilience pattern of useMilesBalanceRealtime: never overwrites
 * with null on transient errors so the UI doesn't flash empty when the
 * network blips. Subscribes to customer_status realtime updates so a tier
 * promotion (e.g. driven by a booking flipping to completed) reflects in the
 * UI immediately.
 */
export function useUserStatus(userId, options = {}) {
  const { isOwnUser = true } = options;
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastSuccessRef = useRef(null);
  const retryRef = useRef(0);
  const refreshTimeoutRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      setIsLoading(false);
      lastSuccessRef.current = null;
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (lastSuccessRef.current !== null) return;
        setStatus(null);
        return;
      }

      const res = await fetch(`${publicOrigin()}/api/status/${userId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        // 4xx/5xx — keep the last known good status to avoid flashing
        if (lastSuccessRef.current !== null) return;
        if (retryRef.current < 1) {
          retryRef.current += 1;
          setTimeout(() => fetchStatus(), 2000);
          return;
        }
        setStatus(null);
        return;
      }
      const json = await res.json();
      lastSuccessRef.current = Date.now();
      setStatus(json);
    } catch (err) {
      console.error('Failed to fetch user status:', err);
      if (lastSuccessRef.current !== null) return;
      if (retryRef.current < 1) {
        retryRef.current += 1;
        setTimeout(() => fetchStatus(), 2000);
        return;
      }
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const refetch = useCallback(() => {
    setIsLoading(true);
    return fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!userId) {
      setStatus(null);
      setIsLoading(false);
      lastSuccessRef.current = null;
      retryRef.current = 0;
      return;
    }
    lastSuccessRef.current = null;
    retryRef.current = 0;
    fetchStatus();
  }, [userId, fetchStatus]);

  // Realtime: refresh when customer_status changes for this user. Bookings
  // also touch this row via the recompute trigger, so this catches both
  // tier-promotion events and admin overrides without separate plumbing.
  useEffect(() => {
    if (!userId || !isOwnUser) return;
    const channel = supabase
      .channel(`customer_status_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_status',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = setTimeout(() => fetchStatus(), 200);
        }
      )
      .subscribe();
    // Polling fallback
    const interval = setInterval(fetchStatus, 60000);
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [userId, isOwnUser, fetchStatus]);

  return { status, isLoading, refetch };
}
